import React, { useState } from "react";
import { Modal } from "./common";
import { useLang } from "../i18n";
import { useConfig } from "../context/ConfigContext";
import api, { apiError } from "../api";
import { toast } from "sonner";

export default function AddStudentModal({ open, onClose, onDone, classes: classesProp }) {
  const { t } = useLang();
  const { config } = useConfig();
  const classes = classesProp || config?.classes || [];
  const emptyForm = {
    name_ar: "",
    name_en: "",
    username: "",
    class_id: "",
    password: "",
    create_login: false,
  };
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name_ar.trim()) { toast.error(t("validation.required")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/staff/students", {
        ...form,
        class_id: form.class_id || classes[0]?.id || null,
        password: form.password || null,
      });
      toast.success(t("teacher.student_added"));
      setCreated(data);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };

  const close = () => { setForm(emptyForm); setCreated(null); onClose(); };

  return (
    <Modal open={open} onClose={close} title={t("teacher.add_student")} testId="add-student-modal">
      {created ? (
        <div className="text-center space-y-4">
          <p className="text-lg font-extrabold text-[#48D276]">{t("teacher.student_added")}</p>
          {created.login_enabled ? (
            <div className="rounded-2xl bg-[#0D0B38]/60 border border-[#FFC83D]/30 p-4" dir="ltr">
              <p className="text-sm text-[#A5A0D6] font-bold">{t("teacher.username")}</p>
              <p data-testid="new-student-username" className="font-black text-lg">
                {created.username}
              </p>
              <p className="text-sm text-[#A5A0D6] font-bold mt-2">
                {t("teacher.temp_password")}
              </p>
              <p data-testid="new-student-password" className="font-black text-lg text-[#FFC83D]">
                {created.temp_password}
              </p>
            </div>
          ) : (
            <p data-testid="profile-only-created" className="font-bold text-[#D8D4F2]">
              {t("teacher.profile_only_created")}
            </p>
          )}
          <button data-testid="add-student-done-btn" onClick={close}
            className="btn-magic w-full">
            {t("common.close")}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-sm font-extrabold block mb-1">
              {t("teacher.full_name_ar")} *
            </label>
            <input data-testid="student-name-ar" className="input-magic" dir="rtl"
              value={form.name_ar} onChange={(e) => set("name_ar", e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-extrabold block mb-1">
              {t("teacher.full_name_en")}
            </label>
            <input data-testid="student-name-en" className="input-magic" dir="ltr"
              value={form.name_en} onChange={(e) => set("name_en", e.target.value)} />
          </div>
          <label data-testid="create-login-toggle"
            className="flex items-center gap-2 text-sm font-extrabold">
            <input data-testid="create-login-checkbox" type="checkbox" checked={form.create_login}
              onChange={(event) => set("create_login", event.target.checked)} />
            {t("teacher.create_login_now")}
          </label>
          {form.create_login && (
            <>
              <div>
                <label className="text-sm font-extrabold block mb-1">
                  {t("teacher.username_optional")}
                </label>
                <input data-testid="student-username" className="input-magic" dir="ltr"
                  value={form.username} onChange={(e) => set("username", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-extrabold block mb-1">
                  {t("teacher.password_optional")}
                </label>
                <input data-testid="student-password" className="input-magic" dir="ltr"
                  value={form.password} onChange={(e) => set("password", e.target.value)} />
              </div>
            </>
          )}
          <div>
            <label className="text-sm font-extrabold block mb-1">
              {t("teacher.class_label")}
            </label>
            <select data-testid="student-class-select" className="input-magic"
              value={form.class_id} onChange={(e) => set("class_id", e.target.value)}>
              {classes.map((c) => (
                <option key={c.id} value={c.id} label={`${c.name_ar} — ${c.name_en}`} />
              ))}
            </select>
          </div>
          <button data-testid="add-student-submit" disabled={busy} onClick={submit}
            className="btn-magic w-full">
            {busy ? t("common.loading") : t("teacher.add_student")}
          </button>
        </div>
      )}
    </Modal>
  );
}
